import { useMutation, useQuery } from '@apollo/client';
import {
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import {
  capitalizeFirstCharacter,
  DisplayErrors,
  PartnerWidgetConfigurationSetting,
} from 'lib-integrations-partner-widgets';
import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import {
  CreatePartnerSettingsConfigurationArg,
  CreatePartnerSettingsConfigurationResponse,
  createWidgetConfigurationMutation,
  PartnerSettingsConfiguration,
  ReadPartnerSettingsConfigurationArg,
  ReadPartnerSettingsConfigurationResponse,
  readWidgetConfigurationByIdQuery,
  UpdatePartnerSettingsConfigurationArg,
  UpdatePartnerSettingsConfigurationResponse,
  updateWidgetConfigurationMutation,
} from 'apis/partnerIntegrations';
import { AppModalBase } from 'app/types/appModal.types';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';

import { ThirdPartySettingsConfiguration } from './components/settingsConfiguration/thirdPartySettingsConfiguration';
import styles from './thirdPartyIntegrationConfigurationModal.module.scss';
import {
  ThirdPartyIntegrationConfigurationModalArg,
  ThirdPartyIntegrationConfigurationModalResult,
} from './thirdPartyIntegrationConfigurationModal.types';

export type SettingsConfigurationFormValues = Record<
  string,
  PartnerWidgetConfigurationSetting & {
    isEditing?: boolean;
  }
>;

/**
 * A modal for configuring 3rd party integrations. This is a wrapper for
 * {@link ThirdPartyIntegrationConfigurationModalImplementation} to provide the
 * GraphQL client inside the modal implementation to support queries and
 * mutations against the partners-api-gateway.
 */
export const ThirdPartyIntegrationConfigurationModal = createAppModal(
  { type: 'app:thirdPartyIntegrationConfiguration' },
  ({ isOpen, modal, hide, meta }): JSX.Element => {
    return (
      <ThirdPartyIntegrationConfigurationModalImplementation
        isOpen={isOpen}
        modalArg={modal.arg}
        meta={meta}
        hide={hide}
      />
    );
  },
);

type ThirdPartyIntegrationConfigurationModalImplementationProps = {
  isOpen: boolean;
  modalArg: ThirdPartyIntegrationConfigurationModalArg;
  meta?: AppModalBase<
    'app:thirdPartyIntegrationConfiguration',
    ThirdPartyIntegrationConfigurationModalArg,
    ThirdPartyIntegrationConfigurationModalResult
  >['meta'];
  hide: VoidFunction;
};

/** A modal for configuring 3rd party integrations */
export const ThirdPartyIntegrationConfigurationModalImplementation = ({
  isOpen,
  modalArg,
  meta,
  hide,
}: ThirdPartyIntegrationConfigurationModalImplementationProps): JSX.Element => {
  const { integrationType, mode, id: integrationId } = modalArg;
  const isCreateMode = mode === 'create';
  const hasNoIntegrationToUpdate = !integrationId && mode === 'update';
  const modalTitle = `${
    mode === 'create' ? 'Create' : 'Update'
  } ${capitalizeFirstCharacter(integrationType)} Integration`;

  const formId = uuid();
  const notify = useNotifications();
  const [integration, setIntegration] = useState<
    PartnerSettingsConfiguration | undefined
  >();

  const defaultFormValues: SettingsConfigurationFormValues = {};
  const form = useForm<SettingsConfigurationFormValues>({
    defaultValues: defaultFormValues,
  });

  const { loading: isIntegrationLoading, error: integrationError } = useQuery<
    ReadPartnerSettingsConfigurationResponse,
    ReadPartnerSettingsConfigurationArg
  >(readWidgetConfigurationByIdQuery, {
    skip: !integrationId,
    variables: {
      id: integrationId as string,
    },
    onCompleted(data) {
      const integrationData = data.Management.widgetConfigurations;
      setIntegration(integrationData[0]);
    },
  });

  const [createConfiguration, { loading: isCreatingWidgetConfiguration }] =
    useMutation<
      CreatePartnerSettingsConfigurationResponse,
      CreatePartnerSettingsConfigurationArg
    >(createWidgetConfigurationMutation, {
      onError: (err) => {
        notify(
          {
            title: 'Error while creating the settings configuration.',
            text: err.cause?.toString() ?? err?.toString(),
          },
          NotificationType.Error,
        );
      },
      onCompleted: (result) => {
        const configuration = result.Management.createWidgetConfiguration;

        notify(
          {
            title: 'Success',
            text: `"${configuration.name}" has been created.`,
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );

        resolveModalResponse(configuration.id);
      },
    });

  const [updateConfiguration, { loading: isUpdatingWidgetConfiguration }] =
    useMutation<
      UpdatePartnerSettingsConfigurationResponse,
      UpdatePartnerSettingsConfigurationArg
    >(updateWidgetConfigurationMutation, {
      onError: (err) => {
        notify(
          {
            title: 'Error while creating the settings configuration.',
            text: err.cause?.toString() ?? err?.toString(),
          },
          NotificationType.Error,
        );
      },
      onCompleted: (result) => {
        const configuration = result.Management.updateWidgetConfiguration;

        notify(
          {
            title: 'Success',
            text: `"${configuration.name}" has been updated.`,
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );

        resolveModalResponse(configuration.id);
      },
    });

  const isLoading =
    isCreatingWidgetConfiguration ||
    isUpdatingWidgetConfiguration ||
    isIntegrationLoading;

  const onSubmit = (formData: SettingsConfigurationFormValues) => {
    const settings = Object.entries(formData)
      .filter(([key, _]) => key !== 'name')
      .map(([key, setting]) => ({
        key,
        value: setting.value,
        secret: setting.secret,
      }));

    if (isCreateMode) {
      createConfiguration({
        variables: {
          type: integrationType,
          // The `enabled` flag controls whether the integration will be shown
          // in the conversation sidebar or not. For now, since we only use this
          // modal in the automations flow, we'll set it to false. It can be
          // changed by an admin in settings/integrations.
          enabled: false,
          name: formData.name.value as string,
          settings,
        },
      });
    } else {
      if (integrationId) {
        updateConfiguration({
          variables: {
            id: integrationId,
            enabled: integration?.enabled ?? false,
            ...(formData.name.value && { name: formData.name.value as string }),
            settings: settings.filter((setting) =>
              setting.secret && !setting.value ? false : true,
            ),
          },
        });
      }
    }
  };

  const resolveModalResponse = useCallback(
    (integrationId: string) => {
      meta?.resolve({
        isSuccessful: true,
        integrationId,
      });

      hide();
    },
    [meta, hide],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={hide}
      className={styles.integrationConfigurationModal}
    >
      <ModalHead
        title={<Text.Heading>{modalTitle}</Text.Heading>}
        onClose={hide}
      />

      <ModalBody>
        <FormProvider {...form}>
          <form id={formId} onSubmit={form.handleSubmit(onSubmit)}>
            <FormGrid>
              {isIntegrationLoading ? (
                <Spinner />
              ) : (
                <ThirdPartySettingsConfiguration
                  mode={mode}
                  integrationType={integrationType}
                  initialValues={integration}
                />
              )}

              {integrationError && (
                <FormField>
                  <DisplayErrors error={integrationError} />
                </FormField>
              )}
            </FormGrid>
          </form>
        </FormProvider>
      </ModalBody>

      <ModalFoot
        confirmName="Save"
        cancelName="Cancel"
        cancelOnClick={hide}
        confirmOnClick={form.handleSubmit(onSubmit)}
        confirmProps={{
          type: 'submit',
          form: formId,
          isLoading,
          disabled: isLoading || hasNoIntegrationToUpdate,
        }}
      />
    </Modal>
  );
};
